/* global DetectUserAgent */
import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";

import useRouter from "hooks/useRouter";
import useTimer from "hooks/useTimer";
import useNotifications from "hooks/useNotifications";
import useGoogleOptimize from "hooks/useGoogleOptimize";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";
import MakePresenter from "presenters/MakePresenter";
import FmvrPresenter from "presenters/FmvrPresenter";
import { noWebGL } from "utils/WebglUtils";
import { castingsToActors } from "utils/makeUtils";
import FmvrRepository from "repositories/FmvrRepository";
import { deserialize, isString } from "utils/storeUtils";
import { fmvrAttributes } from "utils/fmvrUtils";
import { trackTimingFMVR, trackEventVideoRenderedError } from "utils/gAnalyticsUtils";
import { newRelic, jjLogger } from "utils/logUtils";

const PLAYER_TYPES = {
  VIDEOJS: "videojs",
  WEBGL: "webgl",
};

const MAX_RENDER_INTERVALS = 40;

const usePlayer = ({ make, templateGroup }) => {
  const {
    camelizedQuery: { rendering },
  } = useRouter();
  const [fmvr, setFmvr] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);
  const checkingForRender = fmvr == null;
  const hasFmvr = !checkingForRender && isString(FmvrPresenter.url(fmvr));
  const { addErrorNotification } = useNotifications();
  const { endTimer } = useTimer();
  const {
    loadTest,
    loading: loadingGO,
    testValue: { clipPreviewUsesWebGL: useClipPreviewUsesWebGL },
  } = useGoogleOptimize();

  useEffect(() => {
    loadTest("clipPreviewUsesWebGL");
  }, []);

  const trackFMVR = (time) => {
    if (time) {
      trackTimingFMVR(MakePresenter.template(make), time);
    }
  };

  const getVideoJSParams = () => {
    if (!make || !templateGroup) return {};

    const src = TemplateGroupPresenter.isStraightUp(templateGroup)
      ? TemplateGroupPresenter.previewVideo(templateGroup)
      : FmvrPresenter.url(fmvr);
    return {
      src,
      poster: MakePresenter.thumbnail(make),
      loop: TemplateGroupPresenter.isStarringYouClip(templateGroup),
      autoplay: false,
      controls: true,
      onError: () => trackEventVideoRenderedError("FMVR"),
    };
  };

  const id = make && MakePresenter.id(make);
  const previewPage = window.location.href.includes("/view/make-preview/");

  const getWebGlParams = () => {
    if (!make || !templateGroup) return {};
    return {
      poster: MakePresenter.thumbnail(make),
      feature: MakePresenter.webglFeatureVideo(make),
      positionData: MakePresenter.webglPositionData(make),
      actors: castingsToActors(MakePresenter.castings(make)),
      loop: TemplateGroupPresenter.isStarringYouClip(templateGroup),
    };
  };

  const detectPlayerType = () => {
    if (!make || !templateGroup || loadingGO) return false;
    switch (true) {
      case TemplateGroupPresenter.isStraightUp(templateGroup):
        return PLAYER_TYPES.VIDEOJS;
      case rendering && [PLAYER_TYPES.VIDEOJS, PLAYER_TYPES.WEBGL].includes(rendering):
        return rendering;
      case noWebGL:
        return PLAYER_TYPES.VIDEOJS;
      case hasFmvr:
        return PLAYER_TYPES.VIDEOJS;
      case isMobile &&
        TemplateGroupPresenter.isStarringYouClip(templateGroup) &&
        !(useClipPreviewUsesWebGL && previewPage):
        return PLAYER_TYPES.VIDEOJS;
      case DetectUserAgent.isFireFox() && DetectUserAgent.isAndroid():
        return PLAYER_TYPES.VIDEOJS;
      default:
        return PLAYER_TYPES.WEBGL;
    }
  };

  const getPlayerParams = () => {
    const playerType = detectPlayerType();
    if (playerType === PLAYER_TYPES.VIDEOJS) return getVideoJSParams();
    if (playerType === PLAYER_TYPES.WEBGL) return getWebGlParams();
    return null;
  };

  const createFmvr = () =>
    FmvrRepository.create(id, fmvrAttributes(make, templateGroup)).then(
      ({ data }) => {
        const result = deserialize(data);
        if (result && result.url) {
          setFmvr(result);
        }

        return result;
      },
      (error) => {
        return error;
      },
    );

  const fetchFmvr = () =>
    FmvrRepository.show(id).then(
      ({ data }) => {
        const result = deserialize(data);
        setFmvr(result);
        return result;
      },
      (error) => {
        setFmvr(false);
        return error;
      },
    );

  const stopPoll = () => setPollInterval((prevInteval) => clearInterval(prevInteval));

  const pollFmvr = (time = 3000) =>
    new Promise((resolve) => {
      let requestCount = MAX_RENDER_INTERVALS;
      const interval = setInterval(() => {
        fetchFmvr().then((result) => {
          if (result && result.url) {
            trackFMVR(endTimer());
            resolve(result.url);
          } else {
            requestCount -= 1;

            if (requestCount <= 0) {
              const waitTime = (time / 1000) * MAX_RENDER_INTERVALS;
              const message = `FMVR Request Error: Failed to get FMVR render request: Waited ${waitTime} seconds`;
              addErrorNotification();
              newRelic(message);
              jjLogger.log(message);
              stopPoll();
            }
          }
        });
      }, time);
      setPollInterval(interval);
    });

  const hasData = (type, params) => {
    switch (true) {
      case loadingGO:
        return false;
      case PLAYER_TYPES.VIDEOJS === type:
        return !!params.src;
      case PLAYER_TYPES.WEBGL === type:
        return !!params.feature && !!params.actors;
      default:
        return false;
    }
  };

  return {
    detectPlayerType,
    PLAYER_TYPES,
    getPlayerParams,
    fetchFmvr,
    fmvr,
    checkingForRender,
    createFmvr,
    pollFmvr,
    stopPoll,
    pollInterval,
    hasData,
  };
};

export default usePlayer;
